import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Displays low stock message when current variant is below threshold.
 * Keeps itself updated on variant, quantity, and cart changes.
 *
 * @extends {Component}
 */
class LowStockThresholdComponent extends Component {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    this.#attachEventListeners();
    this.#updateVisibility();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  #attachEventListeners() {
    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
  }

  /**
   * @param {import('./events').VariantUpdateEvent} event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newElement = /** @type {HTMLElement | null} */ (event.detail.data.html.querySelector('low-stock-threshold'));

    if (!newElement) {
      this.classList.add('hidden');
      return;
    }

    this.dataset.threshold = newElement.dataset.threshold || '';
    this.dataset.inventoryQuantity = newElement.dataset.inventoryQuantity || '0';
    this.dataset.inventoryManaged = newElement.dataset.inventoryManaged || 'false';
    this.dataset.available = newElement.dataset.available || 'false';

    this.#updateVisibility();
  };

  #updateVisibility() {
    const threshold = Number(this.dataset.threshold);
    const inventoryQuantity = Number(this.dataset.inventoryQuantity);
    const inventoryManaged = this.dataset.inventoryManaged === 'true';
    const available = this.dataset.available === 'true';

    const shouldShow =
      Number.isFinite(threshold) &&
      threshold > 0 &&
      inventoryManaged &&
      available &&
      Number.isFinite(inventoryQuantity) &&
      inventoryQuantity > 0 &&
      inventoryQuantity <= threshold;

    this.classList.toggle('hidden', !shouldShow);
  }
}

if (!customElements.get('low-stock-threshold')) {
  customElements.define('low-stock-threshold', LowStockThresholdComponent);
}
